import { useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { simulateCart } from "~/domains/cart/api/simulateCart";
import type {
  CartSimulationEntry,
  CartSimulationPreviewData,
  CartSimulationPreviewProduct,
  CartSimulationResponseItem,
} from "~/domains/cart/types";

export const CART_SIMULATION_PREVIEW_DEBOUNCE_MS = 1000;

interface UseSimulateCartPreviewProps {
  entries: CartSimulationEntry[];
  enabled: boolean;
}

export interface UseSimulateCartPreviewResult {
  data: CartSimulationPreviewData | null;
  isLoading: boolean;
  isError: boolean;
}

function mapPreviewData(items: CartSimulationResponseItem[]): CartSimulationPreviewData {
  const productsByKey = new Map<string, CartSimulationPreviewProduct>();
  const products: CartSimulationPreviewData["products"] = [];

  items.forEach((item) => {
    const productKey = item.productCode || item.sapMaterialNumber || item.designation;
    let product = productsByKey.get(productKey);

    if (!product) {
      product = {
        designation: item.designation,
        lineItems: [],
      };
      productsByKey.set(productKey, product);
      products.push(product);
    }

    product.lineItems.push({
      packSize: item.packSize,
      packCode: item.packCode,
      quantity: item.quantity,
    });
  });

  return { products };
}

export function useSimulateCartPreview({
  entries,
  enabled,
}: UseSimulateCartPreviewProps): UseSimulateCartPreviewResult {
  const [data, setData] = useState<CartSimulationPreviewData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const structuralKeyRef = useRef<string>("");

  const debouncedRequest = useMemo(
    () =>
      debounce(async (nextEntries: CartSimulationEntry[], requestId: number) => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
          const response = await simulateCart(nextEntries, controller.signal);

          if (controller.signal.aborted || requestId !== requestIdRef.current) {
            return;
          }

          const nextData = mapPreviewData(response);
          setData(nextData.products.length > 0 ? nextData : null);
          setIsError(false);
          setIsLoading(false);
        } catch {
          if (controller.signal.aborted || requestId !== requestIdRef.current) {
            return;
          }

          setData(null);
          setIsError(true);
          setIsLoading(false);
        }
      }, CART_SIMULATION_PREVIEW_DEBOUNCE_MS),
    [],
  );

  useEffect(() => {
    debouncedRequest.cancel();
    abortControllerRef.current?.abort();

    if (!enabled || entries.length === 0) {
      setData(null);
      setIsError(false);
      setIsLoading(false);
      structuralKeyRef.current = "";
      return;
    }

    const currentStructuralKey = entries.length > 0 ? `${entries[0].productCode}|${entries[0].packageCode}` : "";

    if (structuralKeyRef.current && structuralKeyRef.current !== currentStructuralKey) {
      setData(null);
    }

    structuralKeyRef.current = currentStructuralKey;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsError(false);
    setIsLoading(true);
    debouncedRequest(entries, requestId);

    return () => {
      debouncedRequest.cancel();
      abortControllerRef.current?.abort();
    };
  }, [debouncedRequest, enabled, entries]);

  return {
    data,
    isError,
    isLoading,
  };
}
